"use client";

import {
    ShieldCheck,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/components/LanguageContext";
import privacyContentHu from "./privacy.hu";
import privacyContentEn from "./privacy.en";

export default function AdatkezelesiTajekoztatoPage() {
    const {
        language,
        isReady,
    } =
        useLanguage();

    const content =
        language === "en"
            ? privacyContentEn
            : privacyContentHu;

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            <section className="min-h-[calc(100vh-106px)] bg-[#dedede] text-black md:min-h-[calc(100vh-120px)]">


                {content && (
                    <>
                        <PrivacyHeader
                            content={
                                content
                            }
                        />

                        <div className="mx-auto w-full max-w-[430px] px-5 pb-20 md:max-w-5xl md:px-10 md:pb-32">
                            <div className="border-[3px] border-black bg-[#f2f2f2] px-5 py-8 md:px-12 md:py-14">
                                {content.sections.map(
                                    (
                                        section,
                                        index
                                    ) => (
                                        <LegalSection
                                            id={section.id}
                                            key={
                                                section.id ||
                                                section.title
                                            }
                                            number={
                                                index +
                                                1
                                            }
                                            title={
                                                section.title
                                            }
                                            entries={
                                                section.entries
                                            }
                                            tableHeaders={
                                                content.tableHeaders
                                            }
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    </>
                )}
            </section>

            <Footer />
        </main>
    );
}

function PrivacyHeader({
    content,
}) {
    return (
        <div className="mx-auto flex min-h-[330px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-12 text-center md:min-h-[430px] md:max-w-5xl md:px-12 md:py-16">
            <div
                className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
                style={{
                    width:
                        "80px",

                    height:
                        "80px",

                    minWidth:
                        "80px",

                    maxWidth:
                        "80px",

                    minHeight:
                        "80px",

                    maxHeight:
                        "80px",

                    boxSizing:
                        "border-box",
                }}
            >
                <ShieldCheck
                    style={{
                        width:
                            "40px",

                        height:
                            "40px",
                    }}
                    strokeWidth={
                        2.5
                    }
                />
            </div>

            <h1 className="text-[25px] font-black lowercase leading-none md:text-[48px]">
                {content.pageTitle}
            </h1>

            <p className="mt-8 max-w-[320px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-xl md:text-[21px] md:leading-[1.35]">
                {content.introduction}
            </p>

            <div className="mt-6 text-[15px] leading-[1.3] md:text-[18px]">
                <p className="font-black">
                    {content.documentName}
                </p>

                <p className="mt-2 text-neutral-700">
                    {content.effectiveDate}
                </p>
            </div>

            {content.translationNotice && (
                <p className="mt-7 max-w-[330px] border-l-[3px] border-black pl-4 text-left text-[13px] leading-[1.4] text-neutral-600 md:max-w-2xl md:text-[16px]">
                    {content.translationNotice}
                </p>
            )}
        </div>
    );
}

function LegalSection({
    id,
    number,
    title,
    entries,
    tableHeaders,
}) {
    return (
        <section id={id} className="scroll-mt-28 border-t-[3px] border-black py-8 first:border-t-0 first:pt-0 md:py-12">
            <div className="flex items-start gap-4 md:gap-7">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-black text-[13px] font-black text-white md:h-14 md:w-14 md:text-[16px]">
                    {String(
                        number
                    ).padStart(
                        2,
                        "0"
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <h2 className="text-[20px] font-black leading-[1.1] md:text-[30px]">
                        {title}
                    </h2>

                    <div className="mt-6 space-y-5 text-[15px] leading-[1.55] text-neutral-700 md:mt-8 md:text-[18px] md:leading-[1.7]">
                        {entries.map(
                            (
                                entry,
                                index
                            ) => (
                                <LegalEntry
                                    key={
                                        entry.id ||
                                        `${number}-${index}`
                                    }
                                    entry={
                                        entry
                                    }
                                    tableHeaders={
                                        tableHeaders
                                    }
                                />
                            )
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function LegalEntry({
    entry,
    tableHeaders,
}) {
    if (
        typeof entry ===
        "string"
    ) {
        return (
            <p>
                {entry}
            </p>
        );
    }

    if (
        !entry ||
        typeof entry !==
        "object"
    ) {
        return null;
    }

    if (
        entry.type ===
        "heading"
    ) {
        return (
            <h3 className="pt-3 font-black text-black">
                {entry.title}
            </h3>
        );
    }

    if (
        entry.type ===
        "block"
    ) {
        return (
            <DataBlock
                title={
                    entry.title
                }
            >
                {entry.paragraphs.map(
                    (
                        paragraph,
                        index
                    ) => (
                        <p
                            key={
                                `${entry.title}-${index}`
                            }
                        >
                            {paragraph}
                        </p>
                    )
                )}
            </DataBlock>
        );
    }

    if (
        entry.type ===
        "details"
    ) {
        return (
            <div>
                {entry.items.map(
                    (
                        item,
                        index
                    ) => (
                        <p
                            key={
                                `${item}-${index}`
                            }
                            className={
                                index ===
                                0
                                    ? "font-black text-black"
                                    : ""
                            }
                        >
                            {item}
                        </p>
                    )
                )}
            </div>
        );
    }

    if (
        entry.type ===
        "list"
    ) {
        return (
            <ul className="list-disc space-y-2 pl-5 marker:text-black md:pl-7">
                {entry.items.map(
                    (
                        item,
                        index
                    ) => (
                        <li
                            key={
                                `${item}-${index}`
                            }
                        >
                            {item}
                        </li>
                    )
                )}
            </ul>
        );
    }

    if (
        entry.type ===
        "highlight"
    ) {
        return (
            <div className="border-l-[3px] border-black pl-5">
                {entry.items.map(
                    (
                        item,
                        index
                    ) => (
                        <p
                            key={
                                `${item}-${index}`
                            }
                            className={
                                index >
                                0
                                    ? "mt-3"
                                    : ""
                            }
                        >
                            {item}
                        </p>
                    )
                )}
            </div>
        );
    }

    if (
        entry.type ===
        "email"
    ) {
        return (
            <a
                href={`mailto:${entry.value}`}
                className="inline-block cursor-pointer font-black text-black underline underline-offset-4"
            >
                {entry.value}
            </a>
        );
    }

    if (
        entry.type ===
        "table"
    ) {
        return (
            <>
                {entry.title && (
                    <h3 className="pt-3 font-black text-black">
                        {entry.title}
                    </h3>
                )}

                <StorageTable
                    headers={
                        entry.headers ||
                        tableHeaders
                    }
                    rows={
                        entry.rows
                    }
                />
            </>
        );
    }

    return null;
}

function DataBlock({
    title,
    children,
}) {
    return (
        <div className="border-l-[3px] border-black pl-5">
            <h3 className="font-black text-black">
                {title}
            </h3>

            <div className="mt-3 space-y-3">
                {children}
            </div>
        </div>
    );
}

function StorageTable({
    headers,
    rows,
}) {
    return (
        <div className="overflow-x-auto border-[3px] border-black">
            <table className="w-full min-w-[760px] border-collapse text-left text-[13px] leading-[1.45] md:text-[15px]">
                <thead className="bg-black text-white">
                    <tr>
                        {headers.map(
                            (
                                header
                            ) => (
                                <th
                                    key={
                                        header
                                    }
                                    className="px-4 py-3 font-black"
                                >
                                    {header}
                                </th>
                            )
                        )}
                    </tr>
                </thead>

                <tbody className="divide-y-[2px] divide-black bg-white">
                    {rows.map(
                        (
                            row,
                            index
                        ) => (
                            <tr
                                key={
                                    row.id ||
                                    row.name ||
                                    index
                                }
                                className="align-top"
                            >
                                <td className="px-4 py-3 font-black text-black">
                                    {row.name}
                                </td>

                                <td className="px-4 py-3">
                                    {row.type}
                                </td>

                                <td className="px-4 py-3">
                                    {row.purpose}
                                </td>

                                <td className="px-4 py-3">
                                    {row.duration}
                                </td>
                            </tr>
                        )
                    )}
                </tbody>
            </table>
        </div>
    );
}

function LoadingState({
    language,
}) {
    return (
        <div className="flex min-h-[520px] flex-col items-center justify-center px-7 text-center">
            <div className="h-10 w-10 animate-spin rounded-full border-[4px] border-black/20 border-t-black" />

            <p className="mt-5 text-[16px] font-black">
                {language === "en"
                    ? "Loading Privacy Notice..."
                    : "Adatkezelési tájékoztató betöltése..."}
            </p>
        </div>
    );
}

function ErrorState({
    language,
}) {
    return (
        <div className="flex min-h-[520px] flex-col items-center justify-center px-7 text-center">
            <p className="text-[20px] font-black">
                {language === "en"
                    ? "The Privacy Notice could not be loaded."
                    : "Az adatkezelési tájékoztató nem tölthető be."}
            </p>

            <button
                type="button"
                onClick={() => {
                    window.location.reload();
                }}
                className="
                    mt-7 flex h-[48px]
                    min-w-[180px]
                    cursor-pointer items-center
                    justify-center rounded-[6px]
                    bg-[#f5a400] px-6
                    text-[17px] font-black
                    text-black transition-transform
                    hover:scale-[1.03]
                    active:scale-95
                "
            >
                {language === "en"
                    ? "Try again"
                    : "Újrapróbálás"}
            </button>
        </div>
    );
}